/**
 * Direct Cognito User Pools API calls.
 *
 * Shared by auth-service (unauthenticated flows: signup, login, challenges)
 * and credential-service (authenticated flows: passkey management).
 *
 * Uses the JSON-RPC style interface (X-Amz-Target header) rather than
 * the OAuth2 endpoints, enabling passwordless flows without browser redirects.
 */

import { authConfig } from './auth-config.js'
import { logger } from '../../utils/logger.js'

const log = logger.create('cognito-api')

/** Cognito error with typed error code for handling specific cases. */
export class CognitoError extends Error {
  constructor(
    public readonly code: string,
    message: string
  ) {
    super(message)
    this.name = 'CognitoError'
  }
}

/** Cognito User Pools API endpoint for the configured region. */
function endpoint(): string {
  return `https://cognito-idp.${authConfig.region}.amazonaws.com`
}

/** Call a Cognito User Pools API action. */
export async function cognitoCall(
  action: string,
  payload: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const response = await fetch(endpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const body = await response.text()
    log.error(`Cognito ${action} failed:`, response.status, body)

    // Parse Cognito error code (e.g. "UsernameExistsException", "CodeMismatchException")
    let code = 'UnknownError'
    try {
      const parsed = JSON.parse(body)
      code = (parsed.__type as string)?.split('#').pop() ?? code
    } catch {
      // body wasn't JSON
    }

    throw new CognitoError(code, `Cognito ${action} failed: ${response.status}`)
  }

  return response.json()
}

// ── Unauthenticated flows (use ClientId, no AccessToken) ──

export interface SignUpResult {
  userSub: string
  session?: string | undefined
  codeDeliveryDetails?:
    | {
        destination: string
        deliveryMedium: string
        attributeName: string
      }
    | undefined
}

/** Register a new user with email (passwordless). */
export async function signUp(email: string): Promise<SignUpResult> {
  const data = await cognitoCall('SignUp', {
    ClientId: authConfig.clientId,
    Username: email,
    UserAttributes: [{ Name: 'email', Value: email }],
  })

  return {
    userSub: data.UserSub as string,
    session: data.Session as string | undefined,
    codeDeliveryDetails: data.CodeDeliveryDetails
      ? {
          destination: (data.CodeDeliveryDetails as Record<string, string>).Destination,
          deliveryMedium: (data.CodeDeliveryDetails as Record<string, string>).DeliveryMedium,
          attributeName: (data.CodeDeliveryDetails as Record<string, string>).AttributeName,
        }
      : undefined,
  }
}

export interface ConfirmSignUpResult {
  session?: string | undefined
}

/** Confirm registration with the email verification code. */
export async function confirmSignUp(
  email: string,
  code: string,
  session?: string
): Promise<ConfirmSignUpResult> {
  const payload: Record<string, unknown> = {
    ClientId: authConfig.clientId,
    Username: email,
    ConfirmationCode: code,
  }
  if (session) {
    payload.Session = session
  }

  const data = await cognitoCall('ConfirmSignUp', payload)
  return {
    session: data.Session as string | undefined,
  }
}

export interface AuthChallengeResult {
  challengeName?: string | undefined
  session?: string | undefined
  challengeParameters?: Record<string, string> | undefined
  authenticationResult?: AuthenticationResult | undefined
}

export interface AuthenticationResult {
  AccessToken: string
  IdToken: string
  RefreshToken?: string
  ExpiresIn: number
  TokenType: string
}

/** Initiate authentication with USER_AUTH flow. */
export async function initiateAuth(
  email: string,
  preferredChallenge?: 'EMAIL_OTP' | 'WEB_AUTHN',
  session?: string
): Promise<AuthChallengeResult> {
  const authParameters: Record<string, string> = { USERNAME: email }
  if (preferredChallenge) {
    authParameters.PREFERRED_CHALLENGE = preferredChallenge
  }

  const payload: Record<string, unknown> = {
    AuthFlow: 'USER_AUTH',
    ClientId: authConfig.clientId,
    AuthParameters: authParameters,
  }
  if (session) {
    payload.Session = session
  }

  const data = await cognitoCall('InitiateAuth', payload)

  return {
    challengeName: data.ChallengeName as string | undefined,
    session: data.Session as string | undefined,
    challengeParameters: data.ChallengeParameters as Record<string, string> | undefined,
    authenticationResult: data.AuthenticationResult as AuthenticationResult | undefined,
  }
}

/** Respond to an auth challenge (EMAIL_OTP, WEB_AUTHN, SELECT_CHALLENGE). */
export async function respondToAuthChallenge(
  challengeName: string,
  session: string,
  responses: Record<string, string>
): Promise<AuthChallengeResult> {
  const data = await cognitoCall('RespondToAuthChallenge', {
    ChallengeName: challengeName,
    ClientId: authConfig.clientId,
    Session: session,
    ChallengeResponses: responses,
  })

  return {
    challengeName: data.ChallengeName as string | undefined,
    session: data.Session as string | undefined,
    challengeParameters: data.ChallengeParameters as Record<string, string> | undefined,
    authenticationResult: data.AuthenticationResult as AuthenticationResult | undefined,
  }
}

/** Resend the signup confirmation code. */
export async function resendConfirmationCode(email: string): Promise<void> {
  await cognitoCall('ResendConfirmationCode', {
    ClientId: authConfig.clientId,
    Username: email,
  })
}

// ── Authenticated flows (use AccessToken) ──

/** Update user attributes (display name, birthdate, etc). */
export async function updateUserAttributes(
  accessToken: string,
  attributes: Record<string, string>
): Promise<void> {
  const userAttributes = Object.entries(attributes).map(([name, value]) => ({
    Name: name,
    Value: value,
  }))

  await cognitoCall('UpdateUserAttributes', {
    AccessToken: accessToken,
    UserAttributes: userAttributes,
  })
}
